import React, { useEffect, useState } from 'react';
import { validateDisplayName, validateEmail, validatePassword, type UserProfile } from '@big2/shared';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';

interface AccountSettingsSectionProps {
  profile: UserProfile;
}

export function AccountSettingsSection({ profile }: AccountSettingsSectionProps) {
  const { actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'danger'>('profile');

  // Email verification inline state for unverified signed-in users
  const [showVerifyInput, setShowVerifyInput] = useState(false);
  const [verifyOtp, setVerifyOtp] = useState('');

  // Edit profile state
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [email, setEmail] = useState(profile.email);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Delete account state
  const [deletePassword, setDeletePassword] = useState('');
  const [confirmDeleteChecked, setConfirmDeleteChecked] = useState(false);

  useEffect(() => {
    setDisplayName(profile.displayName);
    setEmail(profile.email);
  }, [profile.displayName, profile.email]);

  useEffect(() => {
    if (!profileError) return;
    const timer = setTimeout(() => {
      setProfileError(null);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [profileError]);

  useEffect(() => {
    if (!passwordError) return;
    const timer = setTimeout(() => {
      setPasswordError(null);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [passwordError]);

  const handleVerifyInline = (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifyOtp.trim()) return;
    actions.verifyEmail({
      email: profile.email,
      token: verifyOtp.trim(),
      type: 'signup',
    });
    setVerifyOtp('');
  };

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    const updates: { displayName?: string; email?: string } = {};
    if (displayName.trim() && displayName.trim() !== profile.displayName) {
      const nameRes = validateDisplayName(displayName.trim());
      if (!nameRes.valid) {
        setProfileError(isMn ? nameRes.errorMn! : nameRes.errorEn!);
        return;
      }
      updates.displayName = displayName.trim();
    }
    if (email.trim() && email.trim().toLowerCase() !== profile.email.toLowerCase()) {
      const emailRes = validateEmail(email.trim());
      if (!emailRes.valid) {
        setProfileError(isMn ? emailRes.errorMn! : emailRes.errorEn!);
        return;
      }
      updates.email = email.trim().toLowerCase();
      setShowVerifyInput(true);
    }
    if (updates.displayName === undefined && updates.email === undefined) return;
    actions.updateProfile(updates);
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (!currentPassword) {
      setPasswordError(isMn ? 'Одоогийн нууц үгээ оруулна уу.' : 'Please enter your current password.');
      return;
    }
    const passRes = validatePassword(newPassword);
    if (!passRes.valid) {
      setPasswordError(isMn ? passRes.errorMn! : passRes.errorEn!);
      return;
    }
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordError(isMn ? 'Шинэ нууц үг хоорондоо таарахгүй байна.' : 'New passwords do not match.');
      return;
    }
    actions.changePassword(currentPassword, newPassword);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleDeleteAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmDeleteChecked || deletePassword.length < 6) return;
    actions.deleteAccount(deletePassword);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Unverified Email Banner */}
      {profile.emailVerified === false && (
        <div
          style={{
            background: 'rgba(245, 158, 11, 0.14)',
            border: '1px solid rgba(245, 158, 11, 0.5)',
            borderRadius: 10,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: '0.84em',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#fde68a', fontWeight: 700 }}>
              ⚠️ {isMn ? 'Имэйл хаяг баталгаажаагүй байна' : 'Your email address is not verified yet'}
            </span>
            <button
              type="button"
              onClick={() => {
                setShowVerifyInput(true);
                actions.resendVerification(profile.email);
              }}
              style={{
                background: 'rgba(251, 191, 36, 0.22)',
                color: '#fde68a',
                border: '1px solid rgba(251, 191, 36, 0.5)',
                padding: '4px 10px',
                fontSize: '0.85em',
                borderRadius: 6,
                fontWeight: 700,
              }}
            >
              📧 {isMn ? 'Баталгаажуулах код авах' : 'Send Verification Code'}
            </button>
          </div>

          {showVerifyInput && (
            <form onSubmit={handleVerifyInline} style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <input
                type="text"
                inputMode="numeric"
                value={verifyOtp}
                onChange={(e) => setVerifyOtp(e.target.value)}
                placeholder={isMn ? '6 оронтой код' : '6-digit code'}
                maxLength={12}
                style={{ flex: 1, minWidth: 0, padding: '6px 10px', fontSize: '0.9em', letterSpacing: 2 }}
              />
              <button
                type="submit"
                disabled={verifyOtp.trim().length < 4}
                style={{
                  background: '#10b981',
                  color: '#000',
                  fontWeight: 800,
                  padding: '6px 12px',
                  fontSize: '0.88em',
                }}
              >
                {isMn ? 'Баталгаажуулах' : 'Verify'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Expandable Account Settings Box */}
      <div
        style={{
          background: 'rgba(0,0,0,0.32)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 12,
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fde68a',
            padding: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            fontWeight: 800,
            fontSize: '0.92em',
            cursor: 'pointer',
          }}
        >
          <span>⚙️ {isMn ? 'Бүртгэлийн тохиргоо ба Нууц үг' : 'Account Settings & Security'}</span>
          <span style={{ fontSize: '0.85em', opacity: 0.8 }}>{expanded ? '▲' : '▼'}</span>
        </button>

        {expanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {/* Sub-tabs */}
            <div
              style={{
                display: 'flex',
                background: 'rgba(0,0,0,0.35)',
                borderRadius: 8,
                padding: 3,
                gap: 4,
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('profile')}
                style={{
                  flex: 1,
                  background: activeTab === 'profile' ? 'rgba(255,255,255,0.16)' : 'transparent',
                  color: '#fff',
                  padding: '6px 8px',
                  fontSize: '0.8em',
                  borderRadius: 6,
                }}
              >
                👤 {isMn ? 'Профайл засах' : 'Edit Profile'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('password')}
                style={{
                  flex: 1,
                  background: activeTab === 'password' ? 'rgba(255,255,255,0.16)' : 'transparent',
                  color: '#fff',
                  padding: '6px 8px',
                  fontSize: '0.8em',
                  borderRadius: 6,
                }}
              >
                🔑 {isMn ? 'Нууц үг солих' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('danger')}
                style={{
                  flex: 1,
                  background: activeTab === 'danger' ? 'rgba(248, 113, 113, 0.25)' : 'transparent',
                  color: activeTab === 'danger' ? '#fca5a5' : '#fff',
                  padding: '6px 8px',
                  fontSize: '0.8em',
                  borderRadius: 6,
                }}
              >
                🗑️ {isMn ? 'Бүртгэл устгах' : 'Delete Account'}
              </button>
            </div>

            {activeTab === 'profile' && (
              <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {profileError && (
                  <div style={{ color: '#fca5a5', fontSize: '0.82em' }} role="alert">
                    {profileError}
                  </div>
                )}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <label style={{ fontSize: '0.82em' }}>
                      {isMn ? 'Тоглогчийн нэр' : 'Display Name'}
                    </label>
                    <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>
                      {isMn ? '2–20 тэмдэгт' : '2–20 chars'}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={20}
                    required
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82em' }}>
                    {isMn ? 'Имэйл хаяг' : 'Email Address'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={
                    !displayName.trim() ||
                    !email.trim() ||
                    (displayName.trim() === profile.displayName &&
                      email.trim().toLowerCase() === profile.email.toLowerCase())
                  }
                  style={{ padding: '8px 12px', fontSize: '0.88em' }}
                >
                  💾 {isMn ? 'Өөрчлөлтийг хадгалах' : 'Save Profile Changes'}
                </button>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {passwordError && (
                  <div style={{ color: '#fca5a5', fontSize: '0.82em' }} role="alert">
                    {passwordError}
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82em' }}>
                    {isMn ? 'Одоогийн нууц үг' : 'Current Password'}
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    minLength={1}
                    required
                    placeholder="••••••"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <label style={{ fontSize: '0.82em' }}>
                      {isMn ? 'Шинэ нууц үг' : 'New Password'}
                    </label>
                    <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.5)' }}>
                      {isMn ? '8+ тэмдэгт (үсэг ба тоо)' : '8+ chars (letter & number)'}
                    </span>
                  </div>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                    placeholder="••••••••"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82em' }}>
                    {isMn ? 'Шинэ нууц үг давтах' : 'Confirm New Password'}
                  </label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!currentPassword || newPassword.length < 8}
                  style={{ padding: '8px 12px', fontSize: '0.88em' }}
                >
                  🔐 {isMn ? 'Нууц үг шинэчлэх' : 'Update Password'}
                </button>
              </form>
            )}

            {activeTab === 'danger' && (
              <form onSubmit={handleDeleteAccount} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: '0.82em', color: '#fca5a5', lineHeight: 1.4 }}>
                  ⚠️{' '}
                  {isMn
                    ? 'Бүртгэлээ устгаснаар таны бүх Цай, VIP эрх болон тоглолтын статистик бүрмөсөн устах бөгөөд сэргээх боломжгүй.'
                    : 'Deleting your account permanently removes your profile, Tsai token balance, and game statistics. This action cannot be undone.'}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82em', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={confirmDeleteChecked}
                    onChange={(e) => setConfirmDeleteChecked(e.target.checked)}
                  />
                  <span>
                    {isMn
                      ? 'Би бүртгэлээ бүрмөсөн устгахыг зөвшөөрч байна'
                      : 'I understand and want to permanently delete my account'}
                  </span>
                </label>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.82em' }}>
                    {isMn ? 'Баталгаажуулахын тулд нууц үгээ оруулна уу' : 'Enter Current Password to Confirm'}
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    minLength={6}
                    required
                    placeholder="••••••"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.88em' }}
                  />
                </div>
                <button
                  type="submit"
                  className="danger"
                  disabled={!confirmDeleteChecked || deletePassword.length < 6}
                  style={{ padding: '8px 12px', fontSize: '0.88em', fontWeight: 800 }}
                >
                  🗑️ {isMn ? 'Бүртгэл бүрмөсөн устгах' : 'Permanently Delete Account'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
